import React, { useState, useEffect, useCallback } from 'react';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import CompanyLogo from '../CompanyLogo.png';

const ReportPreview = ({ formData, updateFormData, nextStep, prevStep, currentStep, totalSteps }) => {
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  
  // Helper function to draw a table with borders
  const drawTable = (doc, startX, startY, rows, colWidths, headerRow = false, pageHeight = null, boldRowIndices = []) => {
    let currentY = startY;
    const tableWidth = colWidths.reduce((sum, width) => sum + width, 0);
    const rowHeight = 4; // Further reduced from 5 to 4 for very compact layout
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    
    rows.forEach((row, rowIndex) => {
      // Check for page break before drawing each row (if pageHeight is provided)
      if (pageHeight && currentY + rowHeight > pageHeight - 20) {
        doc.addPage();
        currentY = 25; // Reset to top of new page (matching initial yPosition)
        // Redraw header if needed (optional - you can customize this)
      }
      let currentX = startX;
      const isHeader = headerRow && rowIndex === 0;
      const isBoldRow = boldRowIndices.includes(rowIndex);
      
      // Check if header row should be merged (first cell has text, others are empty)
      const isMergedHeader = isHeader && row[0] && row.slice(1).every(cell => !cell || cell === '');
      
      // Draw row background for header - adjusted for 4mm row height
      if (isHeader) {
        doc.setFillColor(240, 240, 240);
        doc.rect(startX, currentY - 3, tableWidth, rowHeight, 'F'); // Adjusted from -5 to -3
      }
      
      // Draw vertical lines between cells - adjusted for 4mm row height
      let xPos = startX;
      for (let i = 0; i <= colWidths.length; i++) {
        if (i === 0 || i === colWidths.length) {
          // Outer borders - always draw
          doc.setDrawColor(150, 150, 150);
          doc.setLineWidth(0.5);
          doc.line(xPos, currentY - 3, xPos, currentY - 3 + rowHeight); // Adjusted from -5 to -3
        } else if (!isMergedHeader) {
          // Inner borders - skip for merged header
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.3);
          doc.line(xPos, currentY - 3, xPos, currentY - 3 + rowHeight); // Adjusted from -5 to -3
        }
        if (i < colWidths.length) {
          xPos += colWidths[i];
        }
      }
      
      // Draw horizontal line at top of row - adjusted for 4mm row height
      if (rowIndex === 0) {
        doc.setDrawColor(150, 150, 150);
        doc.setLineWidth(0.5);
        doc.line(startX, currentY - 3, startX + tableWidth, currentY - 3); // Adjusted from -5 to -3
      }
      
      // Draw horizontal line at bottom of row - adjusted for 4mm row height
      doc.setDrawColor(150, 150, 150);
      doc.setLineWidth(0.5);
      doc.line(startX, currentY - 3 + rowHeight, startX + tableWidth, currentY - 3 + rowHeight); // Adjusted from -5 to -3
      
      // Draw cells content
      if (isMergedHeader) {
        // Draw merged header cell - centered text spanning all columns
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        const textY = currentY + 0.0;
        doc.text(row[0] || '', startX + tableWidth / 2, textY, { maxWidth: tableWidth - 4, align: 'center' });
      } else {
        // Draw normal cells
        let cellX = startX;
        row.forEach((cell, colIndex) => {
          const cellWidth = colWidths[colIndex];
          
          // Set font style - bold for header row or specified bold rows, normal for data rows
          if (isHeader || isBoldRow) {
            doc.setFont('helvetica', 'bold');
          } else {
            doc.setFont('helvetica', 'normal');
          }
          doc.setFontSize(8);
          
          // Draw cell text - adjusted Y position to prevent overlay with 4mm row height
          const textX = cellX + 2; // Reduced padding
          const textY = currentY + 0.0; // Adjusted to prevent overlay with smaller row height
          doc.text(cell || '', textX, textY, { maxWidth: cellWidth - 4, align: 'left' });
          
          cellX += cellWidth;
        });
      }
      
      currentY += rowHeight;
    });
    
    return currentY;
  };

  // Helper function to draw a horizontal line
  const drawLine = (doc, x1, y1, x2, y2, color = [0, 0, 0], width = 0.5) => {
    doc.setDrawColor(...color);
    doc.setLineWidth(width);
    doc.line(x1, y1, x2, y2);
  };

  // Helper function to check if we need a new page and add one if necessary
  const checkPageBreak = (doc, yPosition, pageHeight, requiredSpace = 20, modelName = null) => {
    const bottomMargin = 20; // Space reserved for footer
    if (yPosition + requiredSpace > pageHeight - bottomMargin) {
      doc.addPage();
      // Redraw header on new page if modelName is provided
      if (modelName) {
        const pageWidth = doc.internal.pageSize.getWidth();
        drawPageHeader(doc, pageWidth, modelName);
        // Add page number to new page
        const pageNum = doc.internal.getCurrentPageInfo().pageNumber;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(`Page ${pageNum}`, pageWidth - 10, pageHeight - 5, { align: 'right' });
      }
      return 25; // Reset to top of new page (same as initial yPosition)
    }
    return yPosition;
  };

  // Helper function to draw header on a new page
  const drawPageHeader = (doc, pageWidth, modelName) => {
    // Header with border - further reduced height
    doc.setFillColor(245, 245, 245);
    doc.rect(0, 0, pageWidth, 20, 'F'); // Further reduced from 25 to 20
    
    // Top border line
    drawLine(doc, 0, 0, pageWidth, 0, [0, 0, 0], 1);
    drawLine(doc, 0, 20, pageWidth, 20, [0, 0, 0], 1);
    
    // Add company logo (left side) - even smaller
    try {
      const logoWidth = 15; // Further reduced from 18 to 15
      const logoHeight = 15; // Further reduced from 18 to 15
      const logoX = pageWidth / 2 - 45; // Adjusted position
      const logoY = 2.5; // Adjusted position
      doc.addImage(CompanyLogo, 'PNG', logoX, logoY, logoWidth, logoHeight);
    } catch (error) {
      console.warn('Could not load company logo:', error);
    }
    
    // Title - Aaditya Mechatronics
    doc.setFontSize(12); // Further reduced from 14 to 12
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Aaditya Mechatronics', pageWidth / 2, 10, { align: 'center' }); // Adjusted Y position
    
    // Subtitle - Test Report - Model name
    doc.setFontSize(8); // Further reduced
    doc.setFont('helvetica', 'normal');
    const subtitle = modelName ? `Test Report - Model ${modelName}` : 'Test Report';
    doc.text(subtitle, pageWidth / 2, 16.5, { align: 'center' }); // Adjusted Y position
  };

  const generatePDF = useCallback(() => {
    // Debug: Log formData to console to verify data is available
    console.log('Generating PDF with formData:', {
      customer: formData.customer,
      poNo: formData.poNo,
      instrumentSlNo: formData.instrumentSlNo,
      invoiceNo: formData.invoiceNo,
      testCondition: formData.testCondition,
      windingTempGradient: formData.windingTempGradient,
      ccuOutput1: formData.ccuOutput1,
      ccuOutput2: formData.ccuOutput2,
      accessories: formData.accessories,
      model: formData.model,
      standardReadings: formData.standardReadings
    });
    
    const doc = new jsPDF('p', 'mm', 'a4'); // Portrait orientation
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 8; // Further reduced from 10 to 8
    const leftMargin = margin;
    const rightMargin = pageWidth - margin;
    
    // Get model name for subtitle
    const modelName = formData.model;
    
    // Header with border - further reduced height
    doc.setFillColor(245, 245, 245);
    doc.rect(0, 0, pageWidth, 20, 'F'); // Further reduced from 25 to 20
    
    // Top border line
    drawLine(doc, 0, 0, pageWidth, 0, [0, 0, 0], 1);
    drawLine(doc, 0, 20, pageWidth, 20, [0, 0, 0], 1);
    
    // Add company logo (left side) - even smaller
    try {
      const logoWidth = 15; // Further reduced from 18 to 15
      const logoHeight = 15; // Further reduced from 18 to 15
      const logoX = pageWidth / 2 - 45; // Adjusted position
      const logoY = 2.5; // Adjusted position
      doc.addImage(CompanyLogo, 'PNG', logoX, logoY, logoWidth, logoHeight);
    } catch (error) {
      console.warn('Could not load company logo:', error);
    }
    
    // Title - Aaditya Mechatronics
    doc.setFontSize(12); // Further reduced from 14 to 12
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Aaditya Mechatronics', pageWidth / 2, 10, { align: 'center' }); // Adjusted Y position
    
    // Subtitle - Test Report - Model name from formData
    doc.setFontSize(8); // Further reduced from 9 to 8
    doc.setFont('helvetica', 'normal');
    const subtitle = modelName ? `Test Report - Model ${modelName}` : 'Test Report';
    doc.text(subtitle, pageWidth / 2, 16.5, { align: 'center' }); // Adjusted Y position
    
    let yPosition = 25; // Further reduced from 30 to 25
    
    // Customer Information Section - Converted to table format with header row
    // Extract values and ensure they're strings
    const customerLabel = String(formData.customer || '_________________');
    const dateValue = String(formData.date || '_____________');
    const poNoValue = String(formData.poNo || '_________________');
    const poDateValue = String(formData.poDate || '_____________');
    const instrumentSlNoValue = String(formData.instrumentSlNo || '_____________');
    const invoiceNoValue = String(formData.invoiceNo || '_________________');
    const invoiceDateValue = String(formData.invoiceDate || '_____________');
    
    // Log the actual values being used
    console.log('Customer Info Values:', {
      customer: customerLabel,
      poNo: poNoValue,
      instrumentSlNo: instrumentSlNoValue,
      invoiceNo: invoiceNoValue
    });
    
    // Customer Information table - adjusted for portrait (180mm available width)
    // Add header row at the top
    const customerInfoRows = [
      ['CUSTOMER INFORMATION', '', '', ''], // Header row spanning all columns
      ['Customer:', customerLabel, 'Date:', dateValue],
      ['P.O. No & Date:', `${poNoValue} & ${poDateValue}`, 'Instrument Sl No:', instrumentSlNoValue],
      ['Invoice no. & date:', `${invoiceNoValue} & ${invoiceDateValue}`, '', '']
    ];
    
    // Column widths: [Label1, Value1, Label2, Value2] - Total: 180mm
    // Balanced widths for labels and values
    const customerInfoColWidths = [45, 55, 40, 40]; // Total: 180mm
    
    yPosition = checkPageBreak(doc, yPosition, pageHeight, 30, modelName);
    yPosition = drawTable(doc, leftMargin, yPosition, customerInfoRows, customerInfoColWidths, true, pageHeight); // Changed to true to show header row as bold
    yPosition += 1; // Keep minimal spacing
    
    // Product Specification Section
    // Build product spec table first to calculate space needed
    const productSpecRows = [
      ['PRODUCT SPECIFICATION', '', '', ''], // Header row spanning all columns
      ['Model No:', formData.modelNo || '4031', 'Particular:', formData.particular ],
      ['Range:', formData.range || '0 - 150 °C', 'No. of Switches:', formData.noOfSwitches || '2'],
      ['Dial Graduation:', formData.dialGraduation || '2 °C', 'Switch Type:', formData.switchType || 'Micro Switch'],
      ['Accuracy:', formData.accuracy || '1.5% of FSD', 'Switch Rating:', formData.switchRating || '5A @ 250VAC & 0.5A @ 250V DC'],
      ['Capillary Length:', formData.capillaryLength || '[Meters]', 'Switch Differential:', formData.switchDifferential || '4 ± 2°C']
    ];
    
    if (formData.accessories) {
      productSpecRows.push(['Accessories:', formData.accessories, '', '']);
    }
    
    // Calculate space needed: table (rows * 4mm per row) + spacing (1mm)
    const productSpecTableHeight = productSpecRows.length * 4; // Updated to 4mm row height
    const productSpecSectionHeight = productSpecTableHeight + 1;
    
    // Check page break BEFORE drawing table to ensure table stays together
    yPosition = checkPageBreak(doc, yPosition, pageHeight, productSpecSectionHeight, modelName);
    
    // Product Spec table: adjusted for portrait (180mm available width)
    const productSpecColWidths = [36, 48, 32, 64]; // Total: 180mm
    yPosition = drawTable(doc, leftMargin, yPosition, productSpecRows, productSpecColWidths, true, pageHeight);
    yPosition += 2;
    
    // Instrument Reading Section
    const temperatures = [40, 60, 80, 100, 120, 140];
    // Adjusted for portrait: 180mm available width
    // Increased first column width to prevent text wrapping in row headers
    const firstColWidth = 70; // Increased from 50 to accommodate long text
    const tempColWidth = 18; // Reduced from 20 to fit within 180mm total
    const totalTableWidth = firstColWidth + (tempColWidth * temperatures.length); // 70 + 108 = 178mm
    
    // Instrument reading table
    // Create header row that spans all columns, then add the data rows
    const instrumentHeaderRow = ['INSTRUMENT READING', ...temperatures.map(() => '')]; // Header row
    const instrumentDataRows = [
      ['STANDARD READING IN °C', ...temperatures.map(t => t.toString())],
      ['Instrument Reading in °C (forward)', ...temperatures.map(t => formData.standardReadings[t]?.forward || t.toString())],
      ['Instrument Reading in °C (reverse)', ...temperatures.map(t => formData.standardReadings[t]?.reverse || '-')]
    ];
    const instrumentReadingRows = [instrumentHeaderRow, ...instrumentDataRows];
    
    // Calculate space needed: table (rows * 4mm per row) + spacing (1mm)
    // Also account for potential CCU rows that might be added
    const instrumentTableHeight = instrumentReadingRows.length * 4; // Updated to 4mm row height
    const instrumentSectionHeight = instrumentTableHeight + 1;
    
    // Check page break BEFORE drawing table to ensure table stays together
    yPosition = checkPageBreak(doc, yPosition, pageHeight, instrumentSectionHeight, modelName);
    
    const instrumentColWidths = [firstColWidth, ...temperatures.map(() => tempColWidth)];
    yPosition = drawTable(doc, leftMargin, yPosition, instrumentReadingRows, instrumentColWidths, true, pageHeight);
    
    // Add CCU Output rows if accessories include CCU
    // Use trim() to handle any whitespace issues and check for partial matches
    const accessories = (formData.accessories || '').trim();
    const showCCUOutput = accessories === "External CCU";
    const showCCUAndRI = accessories === "External CCU and RI";
    const showCCUAndRI1OP = accessories === "External CCU and RI (1 O/P)";
    const showCCUAndRI2OP = accessories === "External CCU and RI (2 O/P)";
    const showCCUAndRI4OP = accessories === "External CCU and RI (4 O/P)";
    
    // Debug logging
    console.log('Accessories check:', {
      accessories,
      showCCUOutput,
      showCCUAndRI,
      showCCUAndRI1OP,
      showCCUAndRI2OP,
      showCCUAndRI4OP,
      ccuOutput1: formData.ccuOutput1,
      ccuOutput2: formData.ccuOutput2
    });
    
    if (showCCUOutput) {
      yPosition = checkPageBreak(doc, yPosition, pageHeight, 20, modelName);
      const ccuRows = [
        ['CCU O/P-1 IN mA', ...temperatures.map(t => formData.ccuOutput1?.[t] || '')],
        ['CCU O/P-2 IN mA', ...temperatures.map(t => formData.ccuOutput2?.[t] || '')]
      ];
      yPosition = drawTable(doc, leftMargin, yPosition, ccuRows, instrumentColWidths, false, pageHeight);
    }
    
    if (showCCUAndRI) {
      yPosition = checkPageBreak(doc, yPosition, pageHeight, 20, modelName);
      const ccuRIRows = [
        ['Remote Indicator Reading in °C (CCU output -1)', ...temperatures.map(t => formData.remoteIndicatorCCU1?.[t] || '')],
        ['CCU O/P-2 IN mA', ...temperatures.map(t => formData.ccuOutput2ForRI?.[t] || '')]
      ];
      yPosition = drawTable(doc, leftMargin, yPosition, ccuRIRows, instrumentColWidths, false, pageHeight);
    }
    
    if (showCCUAndRI1OP) {
      yPosition = checkPageBreak(doc, yPosition, pageHeight, 20, modelName);
      const ccuRI1OPRows = [
        ['Remote Indicator Reading in °C (CCU output -1)', ...temperatures.map(t => formData.remoteIndicatorCCU1For1OP?.[t] || '')],
        ['CCU O/P-2 IN mA', ...temperatures.map(t => formData.ccuOutput2ForRI1OP?.[t] || '')]
      ];
      yPosition = drawTable(doc, leftMargin, yPosition, ccuRI1OPRows, instrumentColWidths, false, pageHeight);
    }
    
    if (showCCUAndRI2OP) {
      yPosition = checkPageBreak(doc, yPosition, pageHeight, 20, modelName);
      const ccuRI2OPRows = [
        ['Remote Indicator Reading in °C (CCU output -1)', ...temperatures.map(t => formData.remoteIndicatorCCU1For2OP?.[t] || '')],
        ['CCU O/P-2 IN mA', ...temperatures.map(t => formData.ccuOutput2ForRI2OP?.[t] || '')]
      ];
      yPosition = drawTable(doc, leftMargin, yPosition, ccuRI2OPRows, instrumentColWidths, false, pageHeight);
    }
    
    if (showCCUAndRI4OP) {
      yPosition = checkPageBreak(doc, yPosition, pageHeight, 40, modelName);
      const ccuRI4OPRows = [
        ['Remote Indicator Reading in °C (CCU output -1)', ...temperatures.map(t => formData.remoteIndicatorCCU1For4OP?.[t] || '')],
        ['CCU O/P-2 IN mA', ...temperatures.map(t => formData.ccuOutput2ForRI4OP?.[t] || '')],
        ['Remote Indicator -O/P-1 IN mA', ...temperatures.map(t => formData.remoteIndicatorOP1ForRI4OP?.[t] || '')],
        ['Remote Indicator -O/P-2 IN mA', ...temperatures.map(t => formData.remoteIndicatorOP2ForRI4OP?.[t] || '')],
        ['Remote Indicator -O/P-3 IN mA', ...temperatures.map(t => formData.remoteIndicatorOP3ForRI4OP?.[t] || '')],
        ['Remote Indicator -O/P-4 IN mA', ...temperatures.map(t => formData.remoteIndicatorOP4ForRI4OP?.[t] || '')]
      ];
      yPosition = drawTable(doc, leftMargin, yPosition, ccuRI4OPRows, instrumentColWidths, false, pageHeight);
    }
    
    yPosition += 2;
    
    // Switch Operation Section
    // Check if model is one of the specified models that should not show Results column
    const shouldHideResults = formData.model && (
      formData.model === '4031-OTI' || 
      formData.model === '4031-WTI' || 
      formData.model === '4031H-OTI' || 
      formData.model === '4031H-WTI'
    );
    
    // Switch operation table
    const switchHeader = shouldHideResults 
      ? ['Switch Name', 'S1', 'S2']
      : ['Switch Name', 'S1', 'Result', 'S2', 'Result'];
    
    // Create header row that spans all columns
    const switchHeaderRow = shouldHideResults 
      ? ['SWITCH OPERATION READING', '', ''] // Header row for 3 columns
      : ['SWITCH OPERATION READING', '', '', '', '']; // Header row for 5 columns
    
    const switchRows = [
      switchHeaderRow,
      switchHeader,
      ['Switch Set Point in °C', formData.s1.setPoint || '80', ...(shouldHideResults ? [] : ['Pass']), formData.s2.setPoint || '90', ...(shouldHideResults ? [] : ['Pass'])],
      ['Switch On at in °C', formData.s1.onAt || '80', ...(shouldHideResults ? [] : ['']), formData.s2.onAt || '90', ...(shouldHideResults ? [] : [''])],
      ['Switch Off at in °C', formData.s1.offAt || '77', ...(shouldHideResults ? [] : ['']), formData.s2.offAt || '87', ...(shouldHideResults ? [] : [''])],
      ['Switch Differential observed in °C', formData.s1.differential || '3', ...(shouldHideResults ? [] : ['']), formData.s2.differential || '3', ...(shouldHideResults ? [] : [''])]
    ];
    
    // Calculate space needed: table (rows * 4mm per row) + spacing (1mm)
    const switchTableHeight = switchRows.length * 4; // Updated to 4mm row height
    const switchSectionHeight = switchTableHeight + 1;
    
    // Check page break BEFORE drawing table to ensure table stays together
    yPosition = checkPageBreak(doc, yPosition, pageHeight, switchSectionHeight, modelName);
    
    // Switch Operation table: adjusted for portrait
    const switchColWidths = shouldHideResults 
      ? [70, 55, 55] // Total: 180mm
      : [60, 30, 25, 30, 35]; // Total: 180mm
    
    yPosition = drawTable(doc, leftMargin, yPosition, switchRows, switchColWidths, true, pageHeight, [1]); // Row index 1 (switchHeader) should be bold
    yPosition += 2;
    
    // Winding Temperature Gradient Reading Section (for WTI models only) - Position 5
    const isWTIModel = formData.model && (formData.model === '4031-WTI' || formData.model === '4031H-WTI');
    if (isWTIModel && formData.windingTempGradient) {
      const gradient = formData.windingTempGradient;
      const gradientRows = [
        ['WINDING TEMPERATURE GRADIENT READING', '', '', '', ''], // Header row spanning all columns
        ['Gradient', 'Initial', 'G1', 'G2', 'G3'],
        [
          'CT Secondary current in Amps',
          gradient.ctSecondaryCurrent?.initial || '0',
          gradient.ctSecondaryCurrent?.g1 || '5',
          gradient.ctSecondaryCurrent?.g2 || '',
          gradient.ctSecondaryCurrent?.g3 || ''
        ],
        [
          '120% overload Current in Amps',
          gradient.overload120Percent?.initial || '-',
          gradient.overload120Percent?.g1 || '-',
          gradient.overload120Percent?.g2 || '6',
          gradient.overload120Percent?.g3 || ''
        ],
        [
          'Maximum continuous current allowed in Amps',
          gradient.maxContinuousCurrent?.initial || '-',
          gradient.maxContinuousCurrent?.g1 || '-',
          gradient.maxContinuousCurrent?.g2 || '6',
          gradient.maxContinuousCurrent?.g3 || ''
        ],
        [
          'REQ. Temp. Rise over Top oil in °C',
          gradient.reqTempRise?.initial || '0',
          gradient.reqTempRise?.g1 || '15',
          gradient.reqTempRise?.g2 || '',
          gradient.reqTempRise?.g3 || ''
        ],
        [
          'WTI reading',
          gradient.wtiReading?.initial || '60',
          gradient.wtiReading?.g1 || '75',
          gradient.wtiReading?.g2 || '',
          gradient.wtiReading?.g3 || ''
        ]
      ];
      
      // Calculate space needed: table (rows * 4mm per row) + spacing (1mm)
      const gradientTableHeight = gradientRows.length * 4; // Updated to 4mm row height
      const gradientSectionHeight = gradientTableHeight + 1;
      
      // Check page break BEFORE drawing table to ensure table stays together
      yPosition = checkPageBreak(doc, yPosition, pageHeight, gradientSectionHeight, modelName);
      
      // Winding Temperature Gradient table: adjusted for portrait (180mm available width)
      const gradientColWidths = [80, 25, 25, 25, 25]; // Total: 180mm
      yPosition = drawTable(doc, leftMargin, yPosition, gradientRows, gradientColWidths, true, pageHeight, [1]); // Row index 1 (Gradient, Initial, G1, G2, G3) should be bold
      yPosition += 2;
    }
    
    // High Voltage Test Section - Position 6
    // Check if model is one of the specified models that should not show Results column
    const shouldHideHvResults = formData.model && (
      formData.model === '4031-OTI' || 
      formData.model === '4031-WTI' || 
      formData.model === '4031H-OTI' || 
      formData.model === '4031H-WTI'
    );
    
    const hvHeader = shouldHideHvResults
      ? ['Equipment Used', 'Test Voltage', 'Test Description']
      : ['Equipment Used', 'Test Voltage', 'Test Description', 'Result'];
    
    // Create header row that spans all columns
    const hvHeaderRow = shouldHideHvResults
      ? ['HIGH VOLTAGE TEST', '', ''] // Header row for 3 columns
      : ['HIGH VOLTAGE TEST', '', '', '']; // Header row for 4 columns
    
    const hvRows = [
      hvHeaderRow,
      hvHeader,
      [
        formData.hvTest.equipment || 'MICRODYNE',
        formData.hvTest.testVoltage || '2.5 KV AC, 50Hz',
        formData.hvTest.testDescription || 'All terminals to Body for 1min.',
        ...(shouldHideHvResults ? [] : [formData.hvTest.result || 'Pass'])
      ]
    ];
    
    // Calculate space needed: table (rows * 4mm per row) + spacing (1mm)
    const hvTableHeight = hvRows.length * 4; // Updated to 4mm row height
    const hvSectionHeight = hvTableHeight + 1;
    
    // Check page break BEFORE drawing table to ensure table stays together
    yPosition = checkPageBreak(doc, yPosition, pageHeight, hvSectionHeight, modelName);
    
    // High Voltage Test table: adjusted for portrait (180mm available width)
    const hvColWidths = shouldHideHvResults
      ? [45, 45, 90] // Total: 180mm
      : [40, 40, 80, 20]; // Total: 180mm
    
    yPosition = drawTable(doc, leftMargin, yPosition, hvRows, hvColWidths, true, pageHeight, [1]); // Row index 1 (hvHeader) should be bold
    yPosition += 2;
    
    // Insulation Resistance Test Section - Position 7
    // Check if model is one of the specified models that should not show Results row
    const shouldHideIrResults = formData.model && (
      formData.model === '4031-OTI' || 
      formData.model === '4031-WTI' || 
      formData.model === '4031H-OTI' || 
      formData.model === '4031H-WTI'
    );
    
    const irRows = [
      ['INSULATION RESISTANCE TEST', ''], // Header row spanning all columns
      ['Parameter', 'Value'],
      ['Instrument', formData.irTest.instrument || 'OTI'],
      ['Test voltage', formData.irTest.testVoltage || '500 VDC'],
      ['Terminal details', formData.irTest.terminalDetails || 'All terminals to earth'],
      ['Measured value before & after HV test', formData.irTest.measuredValue || 'Greater than 100M Ohms']
    ];
    
    if (!shouldHideIrResults) {
      irRows.push(['Result', formData.irTest.result || 'Pass']);
    }
    
    // Calculate space needed: table (rows * 4mm per row) + spacing (1mm)
    const irTableHeight = irRows.length * 4; // Updated to 4mm row height
    const irSectionHeight = irTableHeight + 1;
    
    // Check page break BEFORE drawing table to ensure table stays together
    yPosition = checkPageBreak(doc, yPosition, pageHeight, irSectionHeight, modelName);
    
    // Insulation Resistance Test table: adjusted for portrait (180mm available width)
    const irColWidths = [70, 110]; // Total: 180mm
    yPosition = drawTable(doc, leftMargin, yPosition, irRows, irColWidths, true, pageHeight, [1]); // Row index 1 (Parameter, Value) should be bold
    yPosition += 2;
    
    // Test Condition Section - Position 8
    // Test Condition Section - Position 8
if (formData.testCondition && formData.testCondition.length > 0) {
  yPosition = checkPageBreak(doc, yPosition, pageHeight, 30, modelName);

  // Heading – bold for better visibility
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);
  doc.text('TEST CONDITION', leftMargin, yPosition);
  yPosition += 6; // Increased spacing after heading for better visual separation

  // Body – same font and *table-like* line spacing
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);

  formData.testCondition.forEach((condition, index) => {
    if (condition) {
      yPosition = checkPageBreak(doc, yPosition, pageHeight, 5, modelName);

      const textY = yPosition - 0.5; // match table text baseline
      doc.text(
        `${index + 1}) ${condition}`,
        leftMargin + 2,
        textY,
        { maxWidth: pageWidth - (2 * leftMargin) - 4 }
      );

      yPosition += 4; // 4mm step, same as table rowHeight
    }
  });

  yPosition += 2;
}

    
    // Test Results Section
    // Test Results Section

    // Test Results Section
yPosition = checkPageBreak(doc, yPosition, pageHeight, 30, modelName);

// Heading – bold for better visibility
doc.setFont('helvetica', 'bold');
doc.setFontSize(8);
doc.setTextColor(0, 0, 0);
doc.text('TEST RESULTS', leftMargin, yPosition);
yPosition += 6; // Increased spacing after heading for better visual separation

// Body – same font & table-like spacing
doc.setFont('helvetica', 'normal');
doc.setFontSize(8);
doc.setTextColor(0, 0, 0);

// 🔴 IMPORTANT: use "°C" (degree sign + C), NOT "℃"
const testResults = [
  formData.testResults.indicationAccuracy
    ? '1) Indication accuracy found within ±1.5% of FSD'
    : '1) Indication accuracy NOT found within ±1.5% of FSD',
  formData.testResults.switchOperation
    ? '2) Switch operation is found within ±4°C of switch setting.'
    : '2) Switch operation is NOT found within ±4°C of switch setting.',
  formData.testResults.switchDifferential
    ? '3) Switch differential accuracy is found within 4±2°C of settings.'
    : '3) Switch differential accuracy is NOT found within 4±2°C of settings.',
  formData.testResults.visualFinish
    ? '4) Visual finish found ok.'
    : '4) Visual finish NOT found ok.'
];

testResults.forEach(result => {
  yPosition = checkPageBreak(doc, yPosition, pageHeight, 5, modelName);

  const textY = yPosition - 0.5; // align visually with table rows
  // Simple text call (no maxWidth/options) for consistent rendering
  doc.text(result, leftMargin + 2, textY);

  yPosition += 4; // match table rowHeight
});

yPosition += 2;


    
    // Personnel Section with border - very compact layout
    yPosition = checkPageBreak(doc, yPosition, pageHeight, 15, modelName);
    doc.setFillColor(250, 250, 250);
    const personnelBoxHeight = 14; // Further reduced from 18 to 14
    doc.rect(leftMargin, yPosition - 2, pageWidth - (2 * margin), personnelBoxHeight, 'F');
    drawLine(doc, leftMargin, yPosition - 2, rightMargin, yPosition - 2, [0, 0, 0], 0.5);
    drawLine(doc, leftMargin, yPosition + personnelBoxHeight - 2, rightMargin, yPosition + personnelBoxHeight - 2, [0, 0, 0], 0.5);
    
    // Standardize to Helvetica Normal 8pt
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('Tested By:', leftMargin + 5, yPosition + 3);
    doc.text(formData.testedBy || 'Chethana', leftMargin + 35, yPosition + 3);
    
    // Adjusted for portrait: position "Approved By" to fit within page width
    const approvedByX = pageWidth / 2 + 10;
    doc.text('Approved By:', approvedByX, yPosition + 3);
    doc.text(formData.approvedBy || 'Sharath Kumar', approvedByX + 30, yPosition + 3);
    
    // Signature lines - adjusted for portrait to stay within page bounds
    drawLine(doc, leftMargin + 35, yPosition + 8, leftMargin + 80, yPosition + 8, [150, 150, 150], 0.3);
    const signatureLineEnd = Math.min(approvedByX + 75, rightMargin - 5);
    drawLine(doc, approvedByX + 30, yPosition + 8, signatureLineEnd, yPosition + 8, [150, 150, 150], 0.3);
    
    yPosition += personnelBoxHeight + 1; // Further reduced from +2 to +1
    
    // Footer - compact - standardize to Helvetica Normal 8pt
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text('This is a Computer-Generated Test Certificate', pageWidth / 2, yPosition, { align: 'center' });
    
    // Page number
    const pageNum = doc.internal.getCurrentPageInfo().pageNumber;
    doc.text(`Page ${pageNum}`, pageWidth - 10, pageHeight - 5, { align: 'right' }); // Reduced from -10 to -5
    
    return doc;
  }, [formData]);

  useEffect(() => {
    console.log('PDF Generation triggered - formData changed');
    console.log('Current formData snapshot:', JSON.stringify({
      customer: formData.customer,
      poNo: formData.poNo,
      instrumentSlNo: formData.instrumentSlNo,
      invoiceNo: formData.invoiceNo,
      accessories: formData.accessories,
      model: formData.model
    }, null, 2));
    
    // Generate PDF with current formData
    const doc = generatePDF();
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    
    // Revoke previous URL if it exists
    setPdfPreviewUrl((prevUrl) => {
      if (prevUrl) {
        URL.revokeObjectURL(prevUrl);
      }
      return pdfUrl;
    });
    
    // Cleanup function to revoke the URL when component unmounts or formData changes
    return () => {
      URL.revokeObjectURL(pdfUrl);
    };
  }, [formData, generatePDF]);

  const generateReportText = () => {
    const temperatures = [40, 60, 80, 100, 120, 140];
    
    // Check if model is one of the specified models that should not show Results column
    const shouldHideResults = formData.model && (
      formData.model === '4031-OTI' || 
      formData.model === '4031-WTI' || 
      formData.model === '4031H-OTI' || 
      formData.model === '4031H-WTI'
    );
    
    // Check if model is one of the specified models that should not show Results column for High Voltage Test
    const shouldHideHvResults = formData.model && (
      formData.model === '4031-OTI' || 
      formData.model === '4031-WTI' || 
      formData.model === '4031H-OTI' || 
      formData.model === '4031H-WTI'
    );
    
    // Check if model is one of the specified models that should not show Results row for Insulation Resistance Test
    const shouldHideIrResults = formData.model && (
      formData.model === '4031-OTI' || 
      formData.model === '4031-WTI' || 
      formData.model === '4031H-OTI' || 
      formData.model === '4031H-WTI'
    );
    
    // Build switch operation header row
    const switchHeaderRow = shouldHideResults 
      ? `**SWITCH NAME**                                                                               |                  | **S1** |                        |        |                                 | **S2**                       |         |            |         |            |`
      : `**SWITCH NAME**                                                                               |                  | **S1** |                        |        |                                 | **RESULT**        |         | **S2**                       |         |            |         | **RESULT** |`;
    
    // Build switch set point row
    const switchSetPointRow = shouldHideResults
      ? `Switch Set Point in °C                                                                        |                  | **${formData.s1.setPoint || '80'}** |                        |        |                                 | **${formData.s2.setPoint || '90'}**                       |         |            |         |            |`
      : `Switch Set Point in °C                                                                        |                  | **${formData.s1.setPoint || '80'}** |                        |        |                                 | **Pass**          |         | **${formData.s2.setPoint || '90'}**                       |         |            |         | **Pass**   |`;
    
    let report = `Customer: ${formData.customer || '_________________'}                    |                  |        |                        |        |                                 | Date: ${formData.date || '_____________'}             |         |                              |         |            |         |            |
P o. no & Date: ${formData.poNo || '_________________'} & ${formData.poDate || '_____________'}               |                  |        |                        |        |                                 | Instrument Sl No: ${formData.instrumentSlNo || '_____________'}         |                              |         |            |         |            |
Invoice no. & date: ${formData.invoiceNo || '_________________'} & ${formData.invoiceDate || '_____________'}                                                                               |                  |        |                        |        |                                 |                   |         |                              |         |            |         |            |

**PRODUCT SPECIFICATION**                                                                     |                  |        |                        |        |                                 |                   |         |                              |         |            |         |            |
Model No:                                                                                     | ${formData.modelNo || '4031'}             |        |                        |        | Particular:                     |                   |         | Oil Temperature Indicator    |         |            |         |            |
Range:                                                                                        | ${formData.range || '0 - 150 °C'}       |        |                        |        | No. of Switches:                |                   |         | ${formData.noOfSwitches || '2'}                            |         |            |         |            |
Dial Graduation:                                                                              | ${formData.dialGraduation || '2 °C'}             |        |                        |        | Switch Type:                    |                   |         | ${formData.switchType || 'Micro Switch'}                 |         |            |         |            |
Accuracy:                                                                                     | ${formData.accuracy || '1.5% of FSD'}      |        |                        |        | Switch Rating:                  |                   |         | ${formData.switchRating || '5A @ 250VAC & 0.5A @ 250V DC'} |         |            |         |            |
Capillary Length:                                                                             | ${formData.capillaryLength || '[Meters]'}  |        |                        |        | Switch Differential:            |                   |         | ${formData.switchDifferential || '4 ± 2°C'}                      |         |            |         |            |

**INSTRUMENT READING**                                                                        |                  |        |                        |        |                                 |                   |         |                              |         |            |         |            |
**STANDARD READING IN °C**                                                                    |                  | ${temperatures.map(t => `**${t}**`).join(' | ')}            |
Instrument Reading in °C (forward)                                                            |                  | ${temperatures.map(t => formData.standardReadings[t]?.forward || t).join('     | ')}     |            |
Instrument Reading in °C (reverse)                                                            |                  | ${temperatures.map(t => formData.standardReadings[t]?.reverse || '-').join('     | ')}     |            |

**SWITCH OPERATION READING**                                                                  |                  |        |                        |        |                                 |                   |         |                              |         |            |         |            |
${switchHeaderRow}
${switchSetPointRow}
Switch On at in °C                                                                            |                  | ${formData.s1.onAt || '80'}     |                        |        |                                 |                   |         | ${formData.s2.onAt || '90'}                           |         |            |         |            |
Switch Off at in °C                                                                           |                  | ${formData.s1.offAt || '77'}     |                        |        |                                 |                   |         | ${formData.s2.offAt || '87'}                           |         |            |         |            |
Switch Differential observed in °C                                                            |                  | ${formData.s1.differential || '3'}      |                        |        |                                 |                   |         | ${formData.s2.differential || '3'}                            |         |            |         |            |

**HIGH VOLTAGE TEST**                                                                         |                  |        |                        |        |                                 |                   |         |                              |         |            |         |            |
${shouldHideHvResults 
  ? `**EQUIPMENT USED**                                                                            | **TEST VOLTAGE** |        |                        |        | **TEST DESCRIPTION**            |                   |         |                              |         |            |         |            |
${formData.hvTest.equipment || 'MICRODYNE'}                                                                                     | ${formData.hvTest.testVoltage || '2.5 KV AC, 50Hz'}  |        |                        |        | ${formData.hvTest.testDescription || 'All terminals to Body for 1min.'} |                   |         |                              |         |            |         |            |`
  : `**EQUIPMENT USED**                                                                            | **TEST VOLTAGE** |        |                        |        | **TEST DESCRIPTION**            |                   |         |                              |         | **RESULT** |         |            |
${formData.hvTest.equipment || 'MICRODYNE'}                                                                                     | ${formData.hvTest.testVoltage || '2.5 KV AC, 50Hz'}  |        |                        |        | ${formData.hvTest.testDescription || 'All terminals to Body for 1min.'} |                   |         |                              |         | **${formData.hvTest.result || 'Pass'}**   |         |            |`}

**INSULATION RESISTANCE TEST**                                                                |                  |        |                        |        |                                 |                   |         |                              |         |            |         |            |
**INSTRUMENT**                                                                                |                  |        | **${formData.irTest.instrument || 'OTI'}**                |        |                                 |                   |         |                              |         |            |         |            |
Test voltage                                                                                  |                  |        | ${formData.irTest.testVoltage || '500 VDC'}                |        |                                 |                   |         |                              |         |            |         |            |
Terminal details                                                                              |                  |        | ${formData.irTest.terminalDetails || 'All terminals to earth'} |        |                                 |                   |         |                              |         |            |         |            |
Measured value before & after HV test                                                         |                  |        | ${formData.irTest.measuredValue || 'Greater than 100M Ohms'} |        |                                 |                   |         |                              |         |            |         |            |
${shouldHideIrResults ? '' : `Result                                                                                        |                  |        | **${formData.irTest.result || 'Pass'}**               |        |                                 |                   |         |                              |         |            |         |            |`}

**TEST CONDITION**                                                                            |                  |        |                        |        |                                 |                   |         |                              |         |            |         |            |
1) Continuously stirred oil bath used for calibration & switch test                          |                  |        |                        |        |                                 |                   |         |                              |         |            |         |            |
2) Oil temperature reading taken after 5 minutes, once instrument reaches steady temperature |                  |        |                        |        |                                 |                   |         |                              |         |            |         |            |

**TEST RESULTS**                                                                              |                  |        |                        |        |                                 |                   |         |                              |         |            |         |            |
${formData.testResults.indicationAccuracy ? '1) Indication accuracy found within ±1.5% of FSD' : '1) Indication accuracy NOT found within ±1.5% of FSD'}                                             |                  |        |                        |        |                                 |                   |         |                              |         |            |         |            |
${formData.testResults.switchOperation ? '2) Switch operation is found within ±4℃ of switch setting.' : '2) Switch operation is NOT found within ±4℃ of switch setting.'}                                   |                  |        |                        |        |                                 |                   |         |                              |         |            |         |            |
${formData.testResults.switchDifferential ? '3) Switch differential accuracy is found within 4±2℃ of settings.' : '3) Switch differential accuracy is NOT found within 4±2℃ of settings.'}                            |                  |        |                        |        |                                 |                   |         |                              |         |            |         |            |
${formData.testResults.visualFinish ? '4) Visual finish found ok.' : '4) Visual finish NOT found ok.'}                                                                   |                  |        |                        |        |                                 |                   |         |                              |         |            |         |            |

**Tested By:**                                    |     |     | **Approved By:** |     |
---------------------------------------------------|-----|-----|------------------|-----|
${formData.testedBy || 'Chethana'}                                          |     |     | ${formData.approvedBy || 'Sharath Kumar'}    |     |
**This is a Computer-Generated Test Certificate** |     |     |                  |     |`;

    return report;
  };

  const saveAsTextFile = () => {
    const reportText = generateReportText();
    const blob = new Blob([reportText], { type: 'text/plain;charset=utf-8' });
    const fileName = `AM-QA-TR-F01-${formData.modelNo || '4031'}-${formData.date || new Date().toISOString().split('T')[0]}.txt`;
    saveAs(blob, fileName);
  };

  const saveAsMarkdownFile = () => {
    const reportText = generateReportText();
    const blob = new Blob([reportText], { type: 'text/markdown;charset=utf-8' });
    const fileName = `AM-QA-TR-F01-${formData.modelNo || '4031'}-${formData.date || new Date().toISOString().split('T')[0]}.md`;
    saveAs(blob, fileName);
  };

  const saveAsPDF = () => {
    const doc = generatePDF();
    const fileName = `AM-QA-TR-F01-${formData.modelNo || '4031'}-${formData.date || new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  return (
    <div className="form-section">
      <h3>Report Preview & Save</h3>
      
      {/* Debug section - remove in production */}
      <div style={{ 
        marginBottom: '20px', 
        padding: '10px', 
        backgroundColor: '#f0f0f0', 
        borderRadius: '5px',
        fontSize: '12px'
      }}>
        <strong>Debug Info:</strong>
        <div>Customer: "{formData.customer || 'EMPTY'}"</div>
        <div>P.O. No: "{formData.poNo || 'EMPTY'}"</div>
        <div>Instrument Sl No: "{formData.instrumentSlNo || 'EMPTY'}"</div>
        <div>Invoice No: "{formData.invoiceNo || 'EMPTY'}"</div>
        <div>Test Condition: {formData.testCondition?.length || 0} items</div>
        <div>Winding Temp Gradient: {formData.windingTempGradient ? 'Present' : 'Missing'}</div>
        <div>CCU Output 1: {formData.ccuOutput1 ? 'Present' : 'Missing'}</div>
        <div>Accessories: "{formData.accessories || 'None'}"</div>
        <div>Model: {formData.model || 'Not set'}</div>
        {formData.accessories && (
          <div style={{ color: 'green', fontWeight: 'bold' }}>
            Accessories detected: Will check for CCU outputs
          </div>
        )}
      </div>
      
      <div className="pdf-preview-container">
        {pdfPreviewUrl ? (
          <iframe
            key={pdfPreviewUrl} // Force reload when URL changes
            src={pdfPreviewUrl}
            width="100%"
            height="600px"
            style={{ border: '1px solid #ddd', borderRadius: '5px' }}
            title="PDF Preview"
          />
        ) : (
          <div className="loading-preview">
            <p>Generating PDF preview...</p>
          </div>
        )}
      </div>

      <div className="save-section">
        <h4>Save Report</h4>
        <p>Choose the format to save your test report:</p>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px', flexWrap: 'wrap' }}>
          <button 
            className="btn btn-success" 
            onClick={saveAsPDF}
          >
            Save as PDF (.pdf)
          </button>
          <button 
            className="btn btn-success" 
            onClick={saveAsTextFile}
          >
            Save as Text File (.txt)
          </button>
          <button 
            className="btn btn-success" 
            onClick={saveAsMarkdownFile}
          >
            Save as Markdown File (.md)
          </button>
        </div>
      </div>

      <div className="navigation-buttons">
        <button 
          className="btn btn-secondary" 
          onClick={prevStep}
          disabled={currentStep === 1}
        >
          Previous
        </button>
        <button 
          className="btn btn-primary" 
          onClick={() => window.location.reload()}
        >
          Start New Report
        </button>
      </div>
    </div>
  );
};

export default ReportPreview;
